import { Inject, Injectable, LOCALE_ID, signal } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { Log, User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { ConfigService } from '@fry/lib/config';
import { AuthToken } from '@fry/lib/auth/auth.token';
import { MatDialog } from '@angular/material/dialog';
import { StepupComponent } from './stepup.component';
import { SigninPopupComponent } from './signin-popup.component';
import { SigninErrorComponent } from './signin-error.component';

export interface OidcOptions {
  scope?: string;
  resource?: string;
  organisation?: string;
}

Log.setLogger(console);

@Injectable()
export class AuthOidcService {
  private manager?: UserManager;
  private user?: User | null;
  private _userChanged = new Subject<User>();
  public userChanged = this._userChanged.asObservable();
  private _renewTokenPromise: Promise<User> | null = null;
  private channel: BroadcastChannel | null = null;
  private readonly $loggingOut = signal<boolean>(false);

  constructor(
    private config: ConfigService,
    private authToken: AuthToken,
    private dialog: MatDialog,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.setupBroadcastLogout();
  }

  private setupBroadcastLogout() {
    try {
        this.channel = new BroadcastChannel('eas-logout');

        this.channel.addEventListener('message', (event) => {
            if (event.data?.type === 'logout') {
                if (this.manager) {
                    this.$loggingOut.set(true);
                    console.log('OIDC: Removing user');
                    this.manager.removeUser();
                    const options = this.getLoginSettings({});
                    this.signInPopup(options);
                }
            }
        });
    } catch (err) {
        console.log('Could not setup broadcast logout', err);
    }
  }

  private getLoginSettings(options: {username?: string | null, scope?: string}): OidcOptions {
    const scopes = ['openid', 'profile', 'email'];
    let masquerade: string | null;
    if (options.username === null) {
        localStorage.removeItem('eas:lastMasquerade');
        masquerade = null;
    } else if (options.username) {
        localStorage.setItem('eas:lastMasquerade', options.username);
        masquerade = options.username;
    } else {
        masquerade = localStorage.getItem('eas:lastMasquerade');
    }

    let resource: string | undefined;
    if (masquerade) {
        scopes.push('lase');
        scopes.push(`lase:${masquerade}`);
        resource = `https://risr.global/resources/api+lase:${masquerade}`;
    } else if (options.scope) {
        scopes.push(options.scope);
        resource = `https://risr.global/resources/api+${options.scope}`;
    } else {
      resource = 'https://risr.global/resources/api';
    }

    return {
        scope: scopes.join(' '),
        resource,
    };
}

  async loginAs(username?: string | null) {
    this.startAuthentication(username);
  }

  getExtraQueryParams() {
    const extra: any = {}
    if (this.authToken.organisation) {
      extra.kz_org = this.authToken.organisation;
    }
    const fast = sessionStorage.getItem('fastRedirect');
    if (fast === 'fast') {
      extra.kz_fast_redirect = true
    }
    extra.kz_lang = this.locale;
    return extra;
  };

  initManager(options?: { force: boolean, scope?: string}): void {
    if (options && options.force) {
      this.manager = undefined;
    }

    if (this.manager) {
      return;
    }

    let scope: string = 'openid';

    // If ever we want to use local storage
    const settings: UserManagerSettings = {
      ...this.config.server.oidc,
      automaticSilentRenew: false,
      accessTokenExpiringNotificationTimeInSeconds: 60,
      scope: scope.trim(),
      loadUserInfo: false,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      extraQueryParams: {}
    };

    settings.extraQueryParams = this.getExtraQueryParams();
    this.manager = new UserManager(settings);
  }

  public async init(options?: { force: boolean, scope?: string}): Promise<boolean> {
    console.log('OIDC: Initialising oidc manager');
    this.initManager(options);

    if (!this.manager) {
      throw new Error('No User manager, cannot initialize OIDC!');
    }

    const user = await this.manager.getUser();
    if (!user || user.expired) {
      console.log('OIDC: User is empty');
      let path = location.pathname;
      if (path.startsWith('/org')) {
        path = '/';
      }
      localStorage.setItem('lastUrl', path);

      this.startAuthentication();
      return false;
    }
    console.log('OIDC: New user', user);
    if (user) {
      console.log('OIDC: Expires in ', user.expires_in);
    }
    this.user = user;
    this._userChanged.next(user);
    return true;
  }

  setupEvents() {
    console.log('OIDC: Setting up oidc events');
    if (!this.manager) {
      console.log('No manager yet, cannot set up events');
      return;
    }

    this.manager.events.addUserLoaded((data) => {
      console.log('OIDC: Loaded', data);
      if (data) {
        console.log('OIDC: Expires in ', data.expires_in);
      }
      this.user = data;
      this._userChanged.next(this.user);
    });
    this.manager.events.addUserUnloaded(() => {
      this.user = undefined;
      this._userChanged.next(this.user);
    });

    this.manager.events.addAccessTokenExpiring(() => {
      console.log('OIDC: Expiring');
      if (this.user) {
        console.log('OIDC: Expires in ', this.user.expires_in);
      }
      this.renewToken();
    });

    // this.manager.events.addAccessTokenExpired(() => {
    //   console.log('OIDC: Expired');
    //   this.user = undefined;
    //   this._userChanged.next(this.user);
    // });

    // this.manager.events.addSilentRenewError(err => {
    //   // this.user = undefined;
    //   console.log('OIDC: Silent renew error', err);
    //   this.manager.stopSilentRenew();
    //   setTimeout(() => {
    //     console.log('OIDC: Retrying silent renew');
    //     this.manager.startSilentRenew();
    //   }, 5000);
    //   // this._userChanged.next(this.user);
    //   // this.startAuthenticationPopup();
    // });
  }

  async updateUser() {
    if (!this.manager) {
      console.log('OIDC: Cannot update user because manager does not exist');
      return;
    }
    const user = await this.manager.getUser();
    console.log('OIDC: Updated user from manager', user);
    this.user = user;
    this._userChanged.next(user);
  }

  isLoggedIn(): boolean {
    return this.user != null && !this.user.expired;
  }

  getClaims(): any {
    return this.user && this.user.profile;
  }

  getScope(): any {
    return this.user && this.user.scope;
  }

  getAuthorizationHeaderValue(): string | undefined{
    if (!this.user) {
      return undefined;
    }
    return `${this.user.token_type} ${this.user.access_token}`;
  }

  getToken(): string {
    if (!this.user) {
      return '';
    }
    return this.user.access_token;
  }

  async startAuthenticationPopup(): Promise<User> {
    const options = this.getLoginSettings({});
    const res = await this._startAuthenticationPopup(options);
    return res;
  }

  private async _startAuthenticationPopup(options: OidcOptions): Promise<User> {
    const res = await firstValueFrom(this.dialog.open(StepupComponent).afterClosed());
    if (!res) {
      throw new Error('Step up authentication required');
    }
    if (!this.manager) {
      throw new Error('No User manager, cannot start OIDC!');
    }
    const user = await this.manager.signinPopup(options);
    this.user = user;
    this._userChanged.next(user);
    return user;
  }

  private async signInPopup(options: OidcOptions): Promise<User> {
    if (!this.manager) {
      throw new Error('No User manager, cannot start OIDC!');
    }
    const res = await firstValueFrom(this.dialog.open(SigninPopupComponent, {
      hasBackdrop: true,
      disableClose: true,
    }).afterClosed());
    console.log("OIDC: stepUpPopup", res);
    if (res) {
        this.$loggingOut.set(false);
        return await this.manager.signinPopup(options);
    }
    return Promise.reject({ status: 401, message: 'Sign-in required', code: 'sign-in' });
}

  async completeAuthenticationSilent(): Promise<any> {
    try {
      const user = await this.manager.signinSilentCallback();
      // this.user = user;
      console.log('OIDC: Exp renewal', user);
    } catch (err) {
      console.log('OIDC: Failed renewal', err);
    }
  }

  async completeAuthenticationPopup(): Promise<void> {
    await this.manager.signinPopupCallback();
  }

  startAuthentication(username?: string | null): Promise<void> {
    let path = location.pathname;
    if (path.startsWith('/org')) {
      path = '/';
    }
    const settings = this.getLoginSettings({username: username});
    const extra = this.getExtraQueryParams();

    const options = {
      ...settings,
      extraQueryParams: extra,
      kz_path: path
    }
    return this.manager.signinRedirect(options);
  }

  public async stepUp(): Promise<void> {
    if (!this.manager) {
        throw new Error('No User manager, cannot step up OIDC!');
    }
    const options = this.getLoginSettings({ scope: 'stepup' });
    try {
        const user = await this.manager.signinSilent(options);
        if (!user || !user.scope?.includes('stepup')) {
            throw new Error('Step up authentication required');
        }
    } catch (err) {
        console.log("OIDC: stepUp err - trying popup", err);
        try {
            await this._startAuthenticationPopup(options);
        } catch (err) {
            if (err && err.message === 'Attempted to navigate on a disposed window') {
                var href = location.pathname + location.search + location.hash;
                sessionStorage.setItem('lastUrl', href);
                this.manager.signinRedirect(options);
            }
            if (err && err.error === 'access_denied') {
              const res = await this.dialog.open(SigninErrorComponent, {
                  data: { error: err }
              }).afterClosed().toPromise();
              if (res) {
                  this.startLogout();
              }
            }
            return Promise.reject(err);
        }

    }
}

  public async ensureStepup(): Promise<void> {
    let user = this.user;
    if (user && user.scope.includes('stepup')) {
        return Promise.resolve();
    }

    await this.stepUp();

    user = this.user;
    if (user && user.scope.includes('stepup')) {
        return Promise.resolve();
    }

    return Promise.reject({ status: 401, message: 'Step-up authentication required', code: 'step-up' });
}

  startLogout(): Promise<void> {
    if (!this.manager) {
      throw new Error('No User manager, cannot start OIDC!');
    }
    this.$loggingOut.set(true);
    localStorage.removeItem('eas:lastScope');
    localStorage.removeItem('eas:lastMasquerade');
    if (this.channel) {
        this.channel.postMessage({ type: 'logout' });
    }
    return this.manager.signoutRedirect();
  }

  public async renewToken(opts?: { silent: boolean }): Promise<User> {
    if (!this.manager) {
        throw new Error('No User manager, cannot renew OIDC!');
    }
    if (this.$loggingOut()) {
      throw new Error('Logging out');
    }
    const loginOptions = opts || { silent: false};

    const options = this.getLoginSettings({});
    console.log("OIDC: Requesting renewToken");
    if (this._renewTokenPromise === null) {
        console.log("OIDC: renewToken");
        this._renewTokenPromise = this.manager.signinSilent(options).then(user => {
            console.log("OIDC: renewToken ok");
            this._renewTokenPromise = null;
            if (!user || user.expired) {
                throw new Error('Token renewal failed');
            }
            return user;
        }).catch(err => {
            if (loginOptions.silent) {
                return Promise.reject(err);
            }
            console.log('OIDC: renewToken err - trying popup', err);
            return this.signInPopup(options).then(user => {
                console.log("OIDC: renewToken via popup ok");
                this._renewTokenPromise = null;
                return user;
            }).catch(err => {
                console.log(err);
                if (err && err.message === 'Attempted to navigate on a disposed window') {
                    var href = location.pathname + location.search + location.hash;
                    sessionStorage.setItem('lastUrl', href);
                    this.manager.signinRedirect(options);
                }
                if (err && err.error === 'access_denied') {
                  const res = this.dialog.open(SigninErrorComponent, {
                      data: { error: err }
                  }).afterClosed().toPromise();
                  return res.then(res => {
                      if (res) {
                          this.startLogout();
                      }
                      return Promise.reject(err);
                  });
                }
                return Promise.reject(err);
            });
        }).catch(err => {
            console.log("OIDC: renewToken err", err);
            this._renewTokenPromise = null;
            throw err;
        });
    }
    return this._renewTokenPromise;
  }

  silentRenew(): Promise<User | null> {
    if (!this.manager) {
      throw new Error('No User manager, cannot renew OIDC!');
    }

    const settings = this.getLoginSettings({});
    return this.manager.signinSilent(settings);
  };

  async check() {
    console.log('OIDC: Initialising oidc manager');
    if (!this.manager) {
      return;
    }

    this.manager.clearStaleState();
    return this.manager.querySessionStatus()
    .catch(err => {
      console.log(err);
      const logoutErrors = [
        'login_required',
        'shibcookie_validation_required',
        'consent_required',
        'interaction_required',
      ];
      if (err && logoutErrors.indexOf(err.error) !== -1) {
       return this.manager && this.manager.removeUser();
      }
      return undefined;
    });
  }

  async completeAuthentication(): Promise<void> {
    if (!this.manager) {
      console.log('OIDC: no manager, cannot finish authenticateion');
      return;
    }
    await this.manager.signinRedirectCallback();
  }
}
